import BlockList from "./BlockList";
import { IBlock } from "./BlockList/Block";
import classes from "./classes.module.scss";
import DropdownNavigation from "./DropdownNavigation";
import { useBlockSelection } from "./hooks/useBlockSelection";
import { useBlockSearch } from "./hooks/useBlockSearch";
import { useBottomButton } from "./hooks/useBottomButton";
import { useBlocksSync } from "./hooks/useBlocksSync";
import { useServerSearchHandler } from "./hooks/useServerSearchHandler";
import { SearchBarSection } from "./components/SearchBarSection";
import { BottomButtonSection } from "./components/BottomButtonSection";

export type ISearchBlockListProps = {
	blocks: IBlock[];
	onSelectedBlock: (block: IBlock) => void;
	bottomButton?: {
		text: string;
		link?: string;
		onClick?: () => void;
	};
	fullwidth?: boolean;
	/** Renders above the search bar (e.g. "Dossiers de l'office" / "Dossiers invités" context buttons). */
	contentAboveSearch?: React.ReactNode;
	/** Renders between search bar and folder block list. */
	contentBetweenSearchAndBlocks?: React.ReactNode;
	/**
	 * Champ recherche compact (**`SearchBar` `compact`**). Aligné sur **`SIDEBAR_LIST_COMPACT_SEARCH`** passé en **`sidebarCompactSearch`** depuis **`DefaultDashboardWithList`**.
	 */
	compactSearchBar?: boolean;
	/**
	 * Optional server-side search handler. When provided, the search bar debounces
	 * and delegates filtering to the parent (which reloads the list with `?search=`),
	 * instead of filtering the already-loaded blocks client-side. Used for heavy
	 * offices (e.g. 800+ folders) to avoid loading the whole list before searching.
	 */
	onServerSearch?: (term: string) => void;
};
export default function SearchBlockList(props: ISearchBlockListProps): React.JSX.Element {
	const { blocks, onSelectedBlock, bottomButton, fullwidth = false, contentAboveSearch, contentBetweenSearchAndBlocks, compactSearchBar = false, onServerSearch } = props;

	const { selectedBlock, handleSelectedBlock } = useBlockSelection(blocks, onSelectedBlock);
	const { blocksShowing, handleSearchChange, setBlocksShowing, searchValueRef } = useBlockSearch(blocks);
	const redirectOnBottomButtonClick = useBottomButton(bottomButton);
	// Re-applies the active search filter when new batches arrive (progressive folder load).
	useBlocksSync(blocks, setBlocksShowing, searchValueRef);
	// Server-side search: debounce and delegate to the parent (no client filtering).
	const handleServerSearch = useServerSearchHandler(onServerSearch, searchValueRef);
	const onSearchChange = onServerSearch ? handleServerSearch : handleSearchChange;

	return (
		<div className={classes["root"]} data-fullwidth={fullwidth} data-compact-search={compactSearchBar}>
			{contentAboveSearch != null ? (
				<div className={classes["above-search"]}>{contentAboveSearch}</div>
			) : null}
			<SearchBarSection
				compact={compactSearchBar}
				onSearchChange={onSearchChange}
				bottomButton={bottomButton}
				onBottomButtonClick={redirectOnBottomButtonClick}
			/>
			{contentBetweenSearchAndBlocks != null ? (
				<div className={classes["between-search-and-blocks"]}>{contentBetweenSearchAndBlocks}</div>
			) : null}
			<div className={classes["block-list"]}>
				<BlockList blocks={blocksShowing} onSelectedBlock={handleSelectedBlock} />
			</div>
			<div className={classes["responsive-dropdown"]}>
				<DropdownNavigation
					blocks={blocksShowing}
					onSelectedBlock={handleSelectedBlock}
					defaultSelectedBlock={selectedBlock ?? undefined}
				/>
			</div>
			{bottomButton && <BottomButtonSection bottomButton={bottomButton} onClick={redirectOnBottomButtonClick} />}
		</div>
	);
}
